import { JSX } from "react";

export interface ReportsUIModel {
  analysisType: string;
  asOfUtc: string;
  createDateUtc: string;
  exportType: string;
  fileType: string;
  id: number;
  includeActiveRegionSets: string | boolean;
  includeApprovedRegionSets: string | boolean;
  includeFacilitySpecialties: string | boolean;
  includePendingRegionSets: string | boolean;
  includePhysicianSpecialties: string | boolean;
  isHsd: string | boolean;
  projectId: number;
  projectName: string;
  projectType: string;
  providerCategory: string;
  reportName: JSX.Element | string;
  reportStatusEnum: ReportStatusConverter;
  reportStatus: JSX.Element | string;
  serviceAreaType: string;
  sharedBy: string;
  sharedWith: string[];
  specialtyFilter: string;
  presetName: string;
}

export interface AvailableFilterCriteria {
  availableAnalysisTypes: string[];
  availableReportTypes: string[];
  availableProjectTypes: string[];
  availableProviderCategories: string[];
  availableReportStatuses: string[];
  availableSpecialtyFilters: string[];
}

export enum AnalysisTypeConverter {
  "changed" = "Changed",
  "current" = "Current",
  "projected" = "Projected",
}

export enum ProviderCategoryConverter {
  "all" = "All Providers",
  "available" = "Available Providers",
  "changed" = "Changed Providers",
  "excluded" = "Excluded Providers",
  "recruited" = "Recruited Providers",
  "terminated" = "Terminated Providers",
  "unterminated" = "Unterminated Providers"
}

export enum ReportStatusConverter {
  "complete" = "Complete",
  "failed" = "Failed",
  "initialized" = "Initialized",
  "noDataFound" = "No Data Found",
  "preview" = "Preview",
  "processing" = "Processing",
  "purged" = "Purged",
  "queued" = "Queued",
  "stale" = "Stale",
  "vintage" = "Vintage",
}

export enum SpecialtyFilterConverter {
  "all" = "All Specialties",
  "exceptions" = "Specialties with Exceptions",
  "fillableBySingleProvider" = "Specialties with Gaps Fillable by Single Provider",
  "gaps" = "Specialties with Gaps",
  "fillableGaps" = "Specialties with Fillable Gaps",
  "gapResolutionStatus" = "Specialties with Gap Resolution Status"
}

export enum ExportTypeConverter {
  "accessReportsUsage" = "Access Reports Usage",
  "accuracyActionableProvider" = "Accuracy Actionable Provider",
  "accuracyAggregate" = "Accuracy Aggregate",
  "accuracyServicingProvider" = "Accuracy Servicing Provider",
  "accuracyStatusReport" = "Accuracy Status Report",
  "adequacyAggregate" = "Adequacy Aggregate",
  "adequacyBulkTarget" = "Adequacy Volume Target",
  "adequacyBulkTargetBlob" = "Adequacy Volume Target Blob",
  "adequacyBulkTermination" = "Adequacy Volume Termination",
  "adequacyBulkTerminationBlob" = "Adequacy Volume Termination Blob",
  "adequacyNextClosestProvider" = "Adequacy Next Closest Provider",
  "adequacyServicingProvider" = "Adequacy Servicing Provider",
  "adequacySpecialtySummary" = "Adequacy Specialty Summary",
  "adequacySpecialtySummaryBulk" = "Adequacy Summary Volume",
  "adequacySummary" = "Adequacy Summary",
  "adequacySummaryBulk" = "Adequacy Summary Bulk",
  "adequacyBulkAggregateReport" = "Adequacy Aggregate Volume",
  "attestationAggregate" = "Attestation Aggregate",
  "attestationServicingProvider" = "Attestation Servicing Provider",
  "attestationSummary" = "Attestation Summary",
  "bulkSpecialtyGroupExceptionReport" = "Volume Specialty Group Exception",
  "cciioppfReport" = "CCIIO PPF Report",
  "cciioppfBulkReport" = "CCIIO PPF Bulk Report",
  "cciioProvider" = "CCIIO QHP Download",
  "cciioProviderValidationSummary" = "CCIIO Data Validations Summary",
  "cciioJustificationExport" = "CCIIO Justification Export",
  "cciioValidationDataIntegrity" = "CCIIO Data Validations",
  "closestProvidersToMembers" = "Closest Provider To Member",
  "cmsExceptionReport" = "CMS Exception Report",
  "compositeAggregate" = "Composite Aggregate",
  "compositeServicingProvider" = "Composite Servicing Provider",
  "compositeSummary" = "Composite Summary",
  "distinctServicingProviderReport" = "Distinct Servicing Provider Report",
  "distinctServicingProviderBulkReport" = "Distinct Servicing Provider Bulk Report",
  "expansionOpportunity" = "Expansion Opportunity",
  "hsdNextClosestProvider" = "HSD MA Next Closest Provider",
  "hsdServicingProvider" = "HSD MA Servicing Provider",
  "hsdMmpNextClosestProvider" = "HSD MMP Next Closest Provider",
  "hsdMmpServicingProvider" = "HSD MMP Servicing Provider",
  "hsdServicingProviderBulk" = "HSD Servicing Provider Bulk",
  "multiMapExport" = "Multi Map Export",
  "networkIntegrityProvidersReport" = "Network Integrity Provider",
  "newProviderReport" = "New Provider Report",
  "practitioner" = "Practitioner",
  "practitionerAtLocation" = "Practitioner At Location",
  "providerHubServicingProviderReport" = "Provider Hub Servicing Provider",
  "serviceLocation" = "Service Location",
  "servicingProviderBulk" = "Servicing Provider Volume",
  "summariesDetail" = "Summaries Detail",
  "tARSRepository" = "TARS Repository",
  "userLoginStatistics" = "User Login Statistics",
  "unknown" = "Unknown",
  "zipAnalysisAdequacyServicingProvider" = "Zip Analysis Adequacy Servicing Provider Report",
  "zipAnalysisResultsReport" = "Zip Analysis Results Report",
  "specialtySummaryByZipBulk" = "Specialty Summary by Zip Code Volume",
  "zipClassAggregateResultsReport" = "Zip Class Aggregate Results Report",
  "zipClassAggregateResultsReportBulk" = "Summary by Zip Classification Volume",
  "zipSummaryBulk" = "Zip Summary Volume",
  "zipSummary" = "Zip Summary",
  "zipAnalysisServicingProviderBulk" = "Providers by Zip Code Volume"
}

export enum ServiceAreaTypeConverter {
  "current" = "Current",
  "opportunity" = "Opportunity",
}

export enum ReportFileTypeConverter {
  "compressedBinary" = "Compressed Binary",
  "excel" = "Microsoft Excel Workbook (.xlsx)",
  "jpeg" = "JPEG Image (.jpg)",
  "json" = "JSON",
  "pdf" = "Portable Document Format (.pdf)",
  "png" = "Portable Network Graphics (.png)",
  "textBar" = "Bar-Delimited Text File (.txt)",
  "textComma" = "Comma-Delimited Text File (.csv)",
  "textTab" = "Tab-Delimited Text File (.txt)",
}

export enum ProjectTypeConverter {
  "cACDINetworkAdequacy" = "CACDI Network",
  "cADMHC" = "CADMHC",
  "cCIIOQHPNetworkAdequacy" = "CCIIOQHP Network",
  "iLCAIDNetworkAdequacy" = "ILCAID Network",
  "medicareAdvantageNetworkManagmement" = "Medicare Advantage Network",
  "nJDMAHSNetworkAdequacy" = "NJDMAHS Network",
  "nJDOBINetworkAdequacy" = "NJDOBI Network",
  "nMCAIDNetworkAdequacy" = "NMCAID Network",
  "nPISearch" = "NPI Search",
  "nYSDOHNetworkAdequacy" = "NYSDOH Network",
  "networkAssessment" = "Network Assessment",
  "networkOutreach" = "Network Outreach",
  "pACAIDNetworkAdequacy" = "PACAID Network",
  "tXCAIDNetworkAdequacy" = "TXCAID Network"
}
